import { ChevronRight, Plus, Loader2, CheckCircle2, Activity, ExternalLink } from 'lucide-react';
import { ON_TEAL_TEXT, OPTIMIZATION_BADGE_STYLES } from '../constants';
import type { OptimizationVM, ActionCardStatus, OptimizationTarget } from '../types';

function StatusBadge({ status }: { status: ActionCardStatus }) {
  if (!status.linked) return null;

  if (status.eventStatus === 'Closed') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 6,
          background: 'var(--alpha-teal-10)',
          color: 'var(--alpha-teal)',
          border: '1px solid var(--alpha-teal-20)',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <CheckCircle2 style={{ width: 12, height: 12 }} /> Closed
      </span>
    );
  }
  if (status.eventStatus === 'In-Progress') {
    const progress =
      status.totalArtifacts && status.totalArtifacts > 0
        ? ` (${status.generatedCount ?? 0}/${status.totalArtifacts})`
        : '';
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 8px',
          borderRadius: 6,
          background: 'var(--signal-blue-10)',
          color: 'var(--signal-blue)',
          border: '1px solid var(--signal-blue-20)',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <Activity style={{ width: 12, height: 12 }} /> In-Progress{progress}
      </span>
    );
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 6,
        background: 'var(--muted)',
        color: 'var(--muted-foreground)',
        border: '1px solid var(--border)',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      Open
    </span>
  );
}

// Editorial priority-optimization card (DC §6) with the fix-actions integration
// preserved: unlinked -> "Add to Action Log"; linked -> status badge + "View".
export function OptimizationCard({
  opt,
  status,
  onAdd,
  onView,
  onNavigate,
  gated = false,
}: {
  opt: OptimizationVM;
  status: ActionCardStatus;
  onAdd: (opt: OptimizationVM) => void;
  onView: (opt: OptimizationVM) => void;
  onNavigate: (target: OptimizationTarget) => void;
  /** Action Items is gated off: render the Add button as an honest coming-soon affordance. */
  gated?: boolean;
}) {
  const badge = OPTIMIZATION_BADGE_STYLES[opt.type];
  const isClosed = status.linked && status.eventStatus === 'Closed';

  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        overflow: 'hidden',
        opacity: isClosed ? 0.72 : 1,
        transition:
          'box-shadow var(--duration-base) var(--ease-standard), transform var(--duration-base) var(--ease-standard)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 12px 30px -14px rgba(13,13,15,.16)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
      }}
    >
      {/* left spine */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 3,
          background: 'linear-gradient(var(--alpha-teal),var(--spectral-violet))',
        }}
      />

      <div style={{ display: 'flex', gap: 15 }}>
        <div
          style={{
            flexShrink: 0,
            fontSize: 26,
            fontWeight: 700,
            lineHeight: 1,
            color: 'var(--silver-mist)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-.02em',
          }}
        >
          {opt.idx}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 20,
                padding: '0 8px',
                borderRadius: 6,
                background: badge.bg,
                color: badge.color,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
              }}
            >
              {opt.badge}
            </span>
            <span
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--muted-foreground)',
                background: 'var(--muted)',
                borderRadius: 6,
                padding: '2px 8px',
              }}
            >
              {opt.chipLabel}
            </span>
          </div>
          <h5 style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)', lineHeight: 1.4, margin: '0 0 6px' }}>
            {opt.headline}
          </h5>
          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--muted-foreground)', margin: 0 }}>{opt.desc}</p>
        </div>
      </div>

      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 12, paddingTop: 13, borderTop: '1px solid var(--border)' }}>
        <button
          type="button"
          onClick={() => onNavigate(opt.target)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: 'none',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--spectral-violet)',
            cursor: 'pointer',
          }}
        >
          See how we found this <ChevronRight style={{ width: 13, height: 13 }} />
        </button>

        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          {status.linked ? (
            <>
              <StatusBadge status={status} />
              <button
                type="button"
                onClick={() => onView(opt)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 34,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--card)',
                  color: 'var(--foreground)',
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                <ExternalLink style={{ width: 13, height: 13 }} /> View action
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onAdd(opt)}
              disabled={!!status.adding}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 34,
                padding: '0 14px',
                borderRadius: 8,
                border: gated ? '1px solid var(--border)' : 'none',
                background: gated ? 'var(--muted)' : 'var(--alpha-teal)',
                color: gated ? 'var(--muted-foreground)' : ON_TEAL_TEXT,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                cursor: status.adding ? 'wait' : 'pointer',
                opacity: status.adding ? 0.7 : 1,
                transition: 'filter var(--duration-base) var(--ease-standard)',
              }}
              onMouseEnter={(e) => {
                if (!status.adding && !gated) e.currentTarget.style.filter = 'brightness(1.04)';
              }}
              onMouseLeave={(e) => (e.currentTarget.style.filter = 'none')}
            >
              {status.adding ? (
                <>
                  <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> Adding…
                </>
              ) : gated ? (
                <>Action Log (coming soon)</>
              ) : (
                <>
                  <Plus style={{ width: 13, height: 13 }} /> Add to Action Log
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
